import { FC, useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { MobileToolSheet } from '../../meet/tools-panel/mobile-tool-sheet';
import { loadWheelTips } from '../../../services/tips.api';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { Breakpoint } from '../../../utils/breakpoint';

import { Button, Pressable, Switch } from '../../../shared/ui';
import { TipPages } from '../../horizontal-paginator/TipPages';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { IPsyBlock } from '../new-questions/new-question-psy';

import { IPieTipsItem, ITipItem } from './types';
import { createDefaultWheelConfig, normalizeWheelConfig, type WheelConfig } from './wheel-config';
import { WheelPanel } from './wheelPanel';

export interface ILifeBalanceWheelPsy extends IPsyBlock {
  sendWheelOpen?: (
    payload:
      | {
          values: number[];
          sectorNames?: string[];
          isRealtimeVisible?: boolean;
          isSelectionUnlocked?: boolean;
        }
      | number[],
  ) => void;
  wheelLiveKitEvent?: { nonce: number; kind: 'change' | 'result'; values: number[] } | null;
  onWheelLiveKitEventConsumed?: () => void;
  onSettingsOpenChange?: (open: boolean) => void;
  /**
   * Мобильный 1:1: запуск — в нижней шторке, запущенное колесо — на весь экран в светлой карточке,
   * подсказки открываются нижней шторкой.
   */
  mobile?: boolean;
}

export const LifeBalanceWheelPsy: FC<ILifeBalanceWheelPsy> = ({
  result,
  setResult,
  onToolActivated,
  sendWheelOpen,
  wheelLiveKitEvent,
  onWheelLiveKitEventConsumed,
  onSettingsOpenChange,
  mobile = false,
}) => {
  const [tipsLoading, setTipsLoading] = useState(false);
  const [wheelTips, setWheelTips] = useState<IPieTipsItem[]>([]);

  const [localActive, setActive] = useState(false);
  // На телефоне панель переезжает из шторки (запуск) в основную область (колесо) и пересоздаётся,
  // поэтому «запущено» берём ещё и из состояния инструмента, а не только из локального флага
  const isToolRunning = useAppSelector((state) => state.activeToolStateSlice.activeTool === PsyToolsId.WHEEL);
  const active = localActive || (mobile && isToolRunning);
  const [showTips, setShowTips] = useState(false);
  const [selectedTips, setSelectedTips] = useState(1);
  const [isClientSelecting, setIsClientSelecting] = useState(false);
  const [isRealtimeVisible, setIsRealtimeVisible] = useState(true);
  const [isSelectionUnlocked, setIsSelectionUnlocked] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const breakpoint = useBreakpoint();

  useEffect(() => {
    onSettingsOpenChange?.(settingsOpen || fullscreenOpen);
  }, [settingsOpen, fullscreenOpen, onSettingsOpenChange]);

  const handleSettingsVisibilityChange = useCallback((open: boolean) => {
    setSettingsOpen(open);
  }, []);

  const handleFullscreenChange = useCallback((open: boolean) => {
    setFullscreenOpen(open);
  }, []);

  const [wheelConfig, setWheelConfig] = useState<WheelConfig>(() => createDefaultWheelConfig());

  const dispatch = useAppDispatch();

  const sendConfigToClient = useCallback(
    (config: WheelConfig, realtimeVisible = isRealtimeVisible, selectionUnlocked = isSelectionUnlocked) => {
      const normalized = normalizeWheelConfig(config.values, config.sectorNames);
      sendWheelOpen?.({
        ...normalized,
        isRealtimeVisible: realtimeVisible,
        isSelectionUnlocked: selectionUnlocked,
      });
    },
    [isRealtimeVisible, isSelectionUnlocked, sendWheelOpen],
  );

  const publishConfig = useCallback(
    (config: WheelConfig) => {
      const normalized = normalizeWheelConfig(config.values, config.sectorNames);
      setWheelConfig(normalized);
      if (isRealtimeVisible) {
        sendConfigToClient(normalized);
      }
    },
    [isRealtimeVisible, sendConfigToClient],
  );

  const doSocket = () => {
    sendConfigToClient(wheelConfig);
    dispatch(setActiveTool(PsyToolsId.WHEEL));
    setIsClientSelecting(true);
  };

  const handleRealtimeVisibilityChange = (checked: boolean) => {
    setIsRealtimeVisible(checked);
    sendConfigToClient(wheelConfig, checked);
  };

  const unlockClientSelection = () => {
    setIsSelectionUnlocked(true);
    sendConfigToClient(wheelConfig, isRealtimeVisible, true);
  };

  useEffect(() => {
    if (!wheelLiveKitEvent) return;
    if (wheelLiveKitEvent.kind === 'change') {
      setWheelConfig((prev) => normalizeWheelConfig(wheelLiveKitEvent.values, prev.sectorNames));
      setIsClientSelecting(true);
    } else {
      setWheelConfig((prev) => {
        const normalized = normalizeWheelConfig(wheelLiveKitEvent.values, prev.sectorNames);
        if (result) {
          setResult({
            ...result,
            wheel: [
              ...result.wheel,
              {
                date_time: new Date().toISOString(),
                values: normalized.values,
                sectorNames: normalized.sectorNames,
                comment: '',
              },
            ],
          });
        }
        setIsClientSelecting(false);
        return normalized;
      });
    }
    onWheelLiveKitEventConsumed?.();
  }, [wheelLiveKitEvent, result, setResult, onWheelLiveKitEventConsumed]);

  const titleSize =
    'text-ink text-ui-strong md:text-heading-md xl:text-display-s 2xl:text-display-m largePC:text-display-l';
  const textSize = mobile
    ? 'text-ui'
    : 'text-ui-xs md:text-ui lg:text-body-l xl:text-body-l 2xl:text-body-l largePC:text-heading-lg largePC:font-normal';
  const normalTextSize = `${textSize} font-medium`;
  const commonClassButton = `px-[1rem] !text-center !content-center ${normalTextSize} text-balance cursor-pointer rounded-control`;
  const classButton = `${commonClassButton} bg-surface border-line-strong text-black`;
  const classSelectedButton = `${commonClassButton} bg-primary text-white`;

  const showTip = useCallback(
    (tip: ITipItem) =>
      tip.richtext ? (
        <div
          key="tip_markDown"
          className={`w-full h-full flex flex-col ${tip.richtext.length < 360 ? 'justify-center' : 'pt-[1rem]'} px-[1rem] box-border ${textSize} gap-[2rem]`}
        >
          <ReactMarkdown>{tip.richtext}</ReactMarkdown>
        </div>
      ) : (
        <div key="tip_plain" className="w-full h-full flex flex-col items-center justify-center px-[1rem] box-border">
          <div
            key="tip_qw"
            className={` text-center ${normalTextSize} font-medium `}
            style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: tip.qwestion }}
          >
            {/* {tip.qwestion} */}
          </div>
          {tip.note.length > 0 && (
            <div
              key="tip_note"
              className={` text-center ${normalTextSize} ${mobile ? 'mt-4' : 'mt-[6rem]'} font-normal`}
              style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: tip.note }}
            ></div>
          )}
        </div>
      ),

    [normalTextSize],
  );

  const LeftPanel =
    wheelTips.length > 0 ? (
      <div
        className={classNames('h-full flex flex-col content-between rounded-control bg-surface mt-[0.25rem]', {
          'w-[350px] min-w-[350px] max-w-[350px]': breakpoint >= Breakpoint.Lg,
          'w-[200px] min-w-[200px] max-w-[200px]': breakpoint < Breakpoint.Lg,
        })}
      >
        {wheelTips[selectedTips].tips.length < 2 ? (
          <div className="w-full h-full items-center content-center justify-center ">
            {wheelTips[selectedTips].tips.length === 1 ? showTip(wheelTips[selectedTips].tips[0]) : <></>}
          </div>
        ) : (
          <TipPages
            classOuter={'w-full min-h-[100%] h-full flex flex-col items-center content-center box-border '}
            classIner={'w-full h-full flex overflow-x-hidden scroll-smooth justify-center !items-center'}
            titleKey={wheelTips[selectedTips].title}
            total={wheelTips[selectedTips].tips.length}
            component={(index) => showTip(wheelTips[selectedTips].tips[index])}
          />
        )}
      </div>
    ) : null;

  const onClick = useCallback(async () => {
    if (!showTips && !tipsLoading && wheelTips.length === 0) {
      setTipsLoading(true);
      const { items, index } = await loadWheelTips();
      setWheelTips(items);
      setSelectedTips(index);

      setTipsLoading(false);
    }
    setShowTips((v) => !v);
  }, [showTips, tipsLoading, wheelTips]);

  const wheelPanel = (
    <div className="box-border flex h-full min-h-0 w-full min-w-0 max-w-full flex-col gap-2 overflow-hidden">
      {!isSelectionUnlocked && (
        <div className="box-border mx-0.5 flex w-full min-w-0 max-w-full shrink-0 flex-col gap-2 rounded-control bg-surface-muted px-2 py-1.5 pr-12 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 sm:px-3 sm:py-2 sm:pr-14">
          <div className="min-w-0 flex-1 [&_p]:text-ui-xs [&_p]:leading-snug sm:[&_p]:text-ui md:[&_p]:text-body-l md:[&_p]:leading-normal">
            <Switch
              checked={isRealtimeVisible}
              onChange={handleRealtimeVisibilityChange}
              label="Показывать изменения клиенту в реальном времени"
            />
          </div>
          <Button size="sm" variant="primary" className="shrink-0" onClick={unlockClientSelection}>
            Начать терапию
          </Button>
        </div>
      )}
      <div className="min-h-0 min-w-0 max-w-full flex-1 overflow-hidden">
        <WheelPanel
          values={wheelConfig.values}
          sectorNames={wheelConfig.sectorNames}
          setValues={(nextValues) => {
            setWheelConfig((prev) => normalizeWheelConfig(nextValues, prev.sectorNames));
          }}
          onConfigChange={publishConfig}
          small={showTips && !mobile}
          withTips={showTips}
          onButtonTips={() => onClick()}
          showClientSelectingOverlay={isSelectionUnlocked && isClientSelecting}
          allowSettings={!isSelectionUnlocked}
          onSettingsVisibilityChange={handleSettingsVisibilityChange}
          onFullscreenChange={handleFullscreenChange}
        />
      </div>
    </div>
  );

  const withTips =
    wheelTips.length === 0 ? null : (
      <>
        {LeftPanel}
        <div className="w-full h-full flex flex-col gap-[1rem]">
          <div className="w-full h-[70%]">{wheelPanel}</div>
          <div className={'w-full h-[30%] grid grid-cols-3 grid-rows-[1fr_1fr_1fr] gap-[0.5rem] mx-[0.25rem] '}>
            {wheelTips.map((tip, index) => (
              <div
                key={`wt-${index}`}
                className={index === selectedTips ? classSelectedButton : classButton}
                onClick={() => setSelectedTips(index)}
              >
                {wheelTips[index].title}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  const noTips = wheelPanel;

  const mobileTipsSheet = showTips && (
    <MobileToolSheet title="Подсказки" onClose={() => setShowTips(false)}>
      {tipsLoading && wheelTips.length === 0 ? (
        <p className="py-6 text-center text-ui text-white/80">Загружаем подсказки…</p>
      ) : wheelTips.length > 0 ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-2">
            {wheelTips.map((tip, index) => (
              <Pressable
                key={`mwt-${index}`}
                onClick={() => setSelectedTips(index)}
                aria-pressed={index === selectedTips}
                className={classNames(
                  'rounded-control px-2 py-2 text-center text-ui-xs-strong',
                  index === selectedTips ? 'bg-primary text-white' : 'bg-surface text-ink',
                )}
              >
                {tip.title}
              </Pressable>
            ))}
          </div>
          <div className="min-h-[180px] rounded-control bg-surface py-3 text-ink">
            {wheelTips[selectedTips].tips.length < 2 ? (
              wheelTips[selectedTips].tips.length === 1 ? (
                showTip(wheelTips[selectedTips].tips[0])
              ) : null
            ) : (
              <TipPages
                classOuter="w-full flex flex-col items-center gap-3"
                classIner="w-full flex justify-center !items-center"
                titleKey={wheelTips[selectedTips].title}
                total={wheelTips[selectedTips].tips.length}
                component={(index) => showTip(wheelTips[selectedTips].tips[index])}
              />
            )}
          </div>
        </div>
      ) : (
        <p className="py-6 text-center text-ui text-white/80">Подсказки недоступны.</p>
      )}
    </MobileToolSheet>
  );

  if (mobile && !active) {
    return (
      <div className="flex w-full flex-col gap-3 text-white">
        <p className="text-ui text-white/90">Вы уверены, что хотите включить колесо жизненного баланса?</p>
        <div className="sticky bottom-[-1rem] z-40 -mx-4 bg-ink-muted px-4 pb-4 pt-3 shadow-overlay">
          <Button
            size="lg"
            variant="primary"
            fullWidth
            onClick={() => {
              doSocket();
              onToolActivated?.('life_balance_wheel');
              setActive(true);
            }}
          >
            Включить
          </Button>
        </div>
      </div>
    );
  }

  if (mobile) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col bg-ink-muted px-3 pb-3 pt-1">
        {wheelPanel}
        {mobileTipsSheet}
      </div>
    );
  }

  const initPanel = (
    <div className="sm:w-[30vw] lg:w-[15vw] md:min-w-[300px] h-full flex flex-col">
      <p className="flex mr-[3rem] ml-[1rem] mt-[1rem] text-display-s text-white">Колесо жизненного баланса</p>
      <div className="text-body-l text-white p-[1rem]">
        {'Вы уверены, что хотите включить колесо жизненного баланса?'}
      </div>
      <div className="mt-auto flex flex-row">
        <Button
          size="md"
          variant="primary"
          fullWidth
          onClick={() => {
            doSocket();
            onToolActivated?.('life_balance_wheel');
            setActive(true);
          }}
        >
          {'Включить'}
        </Button>
        <div className="min-w-[48px] w-[48px]"></div>
      </div>
    </div>
  );

  return (
    <>
      {active ? (
        <div className="w-full h-full flex flex-row gap-[0.25rem] relative m-0 p-0">{showTips ? withTips : noTips}</div>
      ) : (
        initPanel
      )}
    </>
  );
};
